import { gql } from '@apollo/client';
import { COMPLETED_TASK_FRAGMENT_WITH_TASK, TASK_FORM_ITEM_FRAGMENT } from '../../../common/form/task-form/model/task-items.gql';

export const GET_VOLUNTEER_COMMON_PROFILE = gql`
  query GetVolunteerCommonProfile(
    $profileId: String!
    $limit: Int
    $offset: Int
  ) {
    vm {
      activityRoles {
        ...VolunteerCommonActivityRole
      }
      volunteerTags(includeArchived: true) {
        ...VolunteerCommonTag
      }
      privilegedPrograms: programs(hasRule: { rule: ReadAllVolunteers }) {
        programId
      }
      profileTask {
        taskId
        title
        description
        order
          cake {
          cakeType
          title
        }
        taskItems {
          ...TaskFormItem
        }
      }
      volunteer(profileId: $profileId) {
        ...VolunteerCommonProfile
      }
      hasActivityRoles
      communicationTemplates {
        communicationTypeId
        name
        status
        type
        isHidden
      }
    }
  }
  ${TASK_FORM_ITEM_FRAGMENT}

  fragment VolunteerCommonProfile on VOLUNTEER_VolunteerType {
    volunteerId
    volunteerStatistics {
      ...MyElementsSideBarVolunteerStatistics
    }
    profile {
      userId
      profileId
      preferredName
      firstName
      lastName
      email
      avatarUrl
      importOperation {
        importOperationId
        importedBy {
          profile {
            firstName
            lastName
          }
        }
        dateCreated
      }
      addedBy {
        profile {
          firstName
          lastName
        }
      }
      dateOfBirth
      description
      contactNumber
      completedProfileTask {
        ...CompletedTaskWithTask
      }
      completedTasks(limit: $limit, offset: $offset) {
        completedTaskId
        dateSubmitted
        status
        task {
          taskId
          title
          adminOnly
        }
      }
      completedRemovedTasks {
        completedTaskId
        dateSubmitted
        status
        task {
          taskId
          title
          adminOnly
          dateRemoved
        }
      }
      metadata {
        totalCompletedTasks
        totalCompletedRemovedTasks
        totalPrograms
      }
      additionalVolunteerHours
    }
    
    dateCreated
    dateDeactivated
    programs {
      programId
      description
      name
    }
    rosterStatus
    tags {
      ...VolunteerCommonTag
    }
    activityRoles {
      ...VolunteerCommonActivityRole
    }
    activityApplications {
      ...VolunteerCommonProfileActivityApplication
    }
    activityWaitlistings {
      ...VolunteerCommonProfileActivityWaitlisting
    }
    flagging {
      dateFlagged
      flaggedReason
      flaggedBy {
        profile {
          profileId
          preferredName
          lastName
          avatarUrl
        }
      }
    }
    application {
      ...VolunteerCommonProfileApplication
    }
    metadata {
      totalActivityEnrolments
      totalActivityApplications
      totalActivityWaitlistings
      totalIncompleteTasks
    }
  }

  ${COMPLETED_TASK_FRAGMENT_WITH_TASK}

  fragment VolunteerCommonActivityRole on VOLUNTEER_ActivityRoleType {
    activityRoleId
    icon
    name
  }

  fragment VolunteerCommonProfileActivityApplication on VOLUNTEER_ActivityApplicationType {
    activityApplicationId
    activity {
      activityId
      name
      program {
        programId
        name
      }
    }
  }

  fragment VolunteerCommonTag on VOLUNTEER_VolunteerTagType {
    volunteerTagId
    name
    colour
    tagGroup
    description
    isArchived
  }

  fragment VolunteerCommonBaseVolunteer on VOLUNTEER_VolunteerType {
    volunteerId
    profile {
      preferredName
      lastName
      email
      avatarUrl
      description
    }
  }
  fragment VolunteerCommonProfileActivityWaitlisting on VOLUNTEER_ActivityWaitlistingType {
    activityWaitlistingId
    activity {
      activityId
      name
      program {
        programId
        name
      }
    }
  }

  fragment VolunteerCommonProfileApplication on VOLUNTEER_ApplicationType {
    applicationId
    name
    email
    dateSubmitted
    # todo: set-up-your-profile
    # documents {
    #   applicantDocumentId
    #   volunteerDocumentType {
    #     volunteerDocumentTypeId
    #     name
    #   }
    #   resourceUrl
    #   submission {
    #     ...Submission
    #   }
    # }
    # formSubmissions {
    #   applicantSubmissionId
    #   submission {
    #     ...Submission
    #   }
    # }
    screeningAnswers {
      screeningAnswerId
      answer
      isCorrect
    }
  }

  fragment MyElementsSideBarVolunteerStatistics on VOLUNTEER_VolunteerStatisticsType {
    attended
    volunteerHours
    sessionReportStatistics {
      ...MyElementsSideBarSessionReportStatistics
    }
  }
  fragment MyElementsSideBarSessionReportStatistics on VOLUNTEER_SessionReportStatisticsType {
    operationalHours
    activityLocations {
      activityLocationId
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_ENROLMENTS = gql`
  query GetVolunteerProfileEnrolments(
    $profileId: String!
    $isHistorical: Boolean
    $rosterStartDate: Date
    $rosterEndDate: Date
  ) {
    vm {
      volunteer(profileId: $profileId) {
        volunteerId
        activityEnrolments(isHistorical: $isHistorical) {
          ...VolunteerCommonProfileActivityEnrolment
        }
      }
    }
  }

  fragment VolunteerCommonProfileActivityEnrolment on VOLUNTEER_ActivityEnrolmentType {
    activityEnrolmentId
    dateRemoved
    dateEnrolled
    enrolledBy {
      profile {
        profileId
        preferredName
        firstName
        lastName
      }
    }
    availabilities {
      ...VolunteerCommonProfileActivityAvailability
    }
    unavailabilities {
      ...VolunteerCommonProfileActivityUnavailability
    }
    activity {
      activityId
      name
      nextX(x: 8)
      ... on VOLUNTEER_NonRecurringActivityType {
        __typename
      }
      startTime
      endTime
      startDate
      endDate
      isRestrictedActivity
      isActive
      isSuspended
      sessions {
        sessionId
      }
      activityLocation {
        placesAddress {
          description
        }
      }
      program {
        programId
        name
      }
      publishedRosters(startDate: $rosterStartDate, endDate: $rosterEndDate) {
        rosterId
        activityDate
        rosterings {
          publishedRosteringId
          dateRemoved
          volunteer {
            volunteerId
          }
        }
      }
      ... on VOLUNTEER_RecurringActivityType {
        schedule {
          recurrences {
            humanReadable
            startDate
            endDate
          }
        }
      }
      activityReports(startDate: $rosterStartDate, endDate: $rosterEndDate) {
        activityDate
        sessionReports {
          sessionReportId
        }
      }
      cancelledActivities(startDate: $rosterStartDate, endDate: $rosterEndDate) {
        ...VolunteerCommonCancelledActivity
      }
      closedActivity {
        ...VolunteerCommonClosedActivity
      }
      draftRosters(startDate: $rosterStartDate, endDate: $rosterEndDate) {
        ...VolunteerCommonDraftRoster
      }
    }
  }
  fragment VolunteerCommonProfileActivityAvailability on VOLUNTEER_ActivityAvailabilityType {
    activityDate
    isActive
    activityRoles {
      activityRoleId
      name
    }
  }
  fragment VolunteerCommonProfileActivityUnavailability on VOLUNTEER_ActivityUnavailabilityType {
    activityDate
    isActive
  }
  fragment VolunteerCommonCancelledActivity on VOLUNTEER_CancelledActivityType {
    cancelledActivityId
    activityDate
  }
  fragment VolunteerCommonClosedActivity on VOLUNTEER_ClosedActivityType {
    closedActivityId
  }
  fragment VolunteerCommonDraftRoster on VOLUNTEER_DraftRosterType {
    draftRosterId
    activityDate
    publishedRoster {
      rosterId
      dateRemoved
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_INCOMPLETE_TASKS = gql`
  query GetVolunteerProfileIncompleteTasks($profileId: String!) {
    vm {
      volunteer(profileId: $profileId) {
        volunteerId
        tasks {
          taskId
          title
          order
          adminOnly
          isRefereeCheckTask
          isEventTask
          cake {
            cakeType
          }
          dateRemoved
        }
      }
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_TASKS = gql`
  query GetVolunteerProfileTasks($userId: String!) {
    vm {
      profile(userId: $userId) {
        profileId
        completedTasks {
          completedTaskId
          dateLastUpdated
          task {
            adminOnly
            taskId
            title
          }
        }
      }
    }
  }
`;

export const GET_VOLUNTEER_PROFILE_COMMUNICATION_TEMPLATES = gql`
  query GetVolunteerProfileCommunicationTemplates {
    vm {
      communicationTemplates {
        ...CommunicationTemplate
      }
    }
    im {
      communicationTemplates {
        ...CommunicationTemplate
      }
    }
  }
  fragment CommunicationTemplate on VOLUNTEER_CommunicationTemplateType {
    communicationTypeId
    name
    tag
  }
`;

